/**
 * Field Help — Contacts
 *
 * Keys MUST match the contact field ids in
 * `features/contacts/config/contact-fields-*.ts` (see CONTACT_FIELDS /
 * ContactFieldId) so the "?" popover attaches to the right column / editor row
 * / card field.
 *
 * Relationship context (used in `relatedTo`): a contact works for exactly ONE
 * company (the employer, via company_id). Tickets, communications, and tasks
 * attach directly to the contact. Deals do NOT link to a contact directly —
 * they reach the contact THROUGH the company. Contact creation goes through the
 * insert_contact() RPC because normalized_name is a generated column. See
 * `lib/actions/relationships/AGENTS.md`.
 */

import type { EntityFieldHelp } from "./types";

export const contactFieldHelp: EntityFieldHelp = {
  name: {
    title: "Name",
    description: "The person's full name. Required on every contact.",
    usage: "The headline on the contact card and in every list, search result, and reminder. Use the name they go by professionally.",
  },
  email: {
    title: "Email",
    description: "The contact's primary email address.",
    usage: "Used to send and track email from the CRM, and the key the system dedups on when leads come in from funnels. Keep it current so outreach lands.",
    relatedTo: "Drives Send Email from the contact. Email aliases hold any additional addresses.",
  },
  phone: {
    title: "Phone",
    description: "The contact's primary phone number.",
    usage: "Powers Call and Send Text actions — keep it in a dialable format. A missing phone blocks click-to-call and SMS from the contact.",
    relatedTo: "Phone aliases hold shared company lines and alternate numbers; OpenPhone logs to every contact that matches.",
  },
  title: {
    title: "Job Title",
    description: "The contact's role at their company (e.g. \"IT Director\").",
    usage: "Helps you gauge decision-making authority before reaching out. Useful for prioritizing who to contact at an account.",
  },
  company_name: {
    title: "Company Name",
    description: "Display name of the contact's employer (denormalized from the linked company).",
    usage: "Read-only label for scanning lists. Change the linked Company to change this.",
    relatedTo: "Mirrors the linked Company record; set via the Company field.",
  },
  company_id: {
    title: "Company",
    description: "The company this contact works for. A contact belongs to exactly one company.",
    usage: "Set this to tie the person to their employer. It's also how deals reach this person — deals attach to the company, and the contact is reached through it.",
    relatedTo: "Contact → company (employer). Deals link to the company, never to the contact directly.",
  },
  has_active_deals: {
    title: "Company Has Deals",
    description: "Whether the contact's company has at least one active sales-pipeline deal.",
    usage: "Read-only. A quick signal that there's live opportunity at this person's account — useful for prioritizing follow-up.",
    relatedTo: "Mirrored from the linked company's deal metrics. Updates when the company link or its deals change.",
  },
  active_deal_count: {
    title: "Company Active Deals",
    description: "Count of active sales-pipeline deals at the contact's company.",
    usage: "Read-only. Higher counts flag accounts with the most in-flight opportunity.",
    relatedTo: "Mirrored from the linked company's deal metrics.",
  },
  address: {
    title: "Address",
    description: "The contact's full street address.",
    usage: "On-site visits, shipping, and billing context. Optional — fill in when location matters for the engagement.",
  },
  city: {
    title: "City",
    description: "City portion of the contact's address.",
    usage: "Useful for territory segmentation and filtering contacts by area.",
  },
  state: {
    title: "State",
    description: "State portion of the contact's address.",
    usage: "Useful for territory segmentation and regional reporting.",
  },
  zip_code: {
    title: "Zip Code",
    description: "Postal code portion of the contact's address.",
    usage: "Supports mailing and local-area segmentation.",
  },
  country: {
    title: "Country",
    description: "Country portion of the contact's address.",
    usage: "Defaults to the contact's region; set only when working internationally.",
  },
  status: {
    title: "Status",
    description: "Where the contact sits in the relationship: prospect, lead, customer, or inactive.",
    usage: "Drives saved-view segmentation (Customers, Leads, Prospects) and who shows up for outreach. Move it forward as the relationship progresses. Status changes are admin-only.",
  },
  source: {
    title: "Source",
    description: "How the contact entered the CRM (e.g. funnel, referral, manual, webhook).",
    usage: "Read-mostly attribution — shows which channels produce contacts. Funnel leads set this automatically on intake. Admin-managed.",
  },
  tags: {
    title: "Tags",
    description: "Free-form labels for segmenting contacts (e.g. vip, priority, follow-up).",
    usage: "Your own segmentation layer — filter and bulk-act on tagged contacts. Supports custom tags up to ten per contact.",
  },
  is_active: {
    title: "Active",
    description: "Whether the contact record is active.",
    usage: "Turn off to retire a contact without deleting it — keeps history while removing them from active working lists.",
  },
  lifecycle_stage: {
    title: "Lifecycle Stage",
    description: "Marketing-style lifecycle position (e.g. subscriber, lead, customer).",
    usage: "A finer-grained relationship marker alongside Status, useful for nurture and reporting.",
  },
  email_aliases: {
    title: "Email Aliases",
    description: "Additional email addresses this contact uses beyond the primary email.",
    usage: "Add alternate addresses so inbound mail from any of them matches this contact. Admin-only.",
    relatedTo: "Supplements the primary Email field.",
  },
  phone_aliases: {
    title: "Phone Aliases",
    description: "Additional phone numbers for this contact (shared company lines, direct lines, etc.).",
    usage: "When OpenPhone receives a call or text on any listed number, the communication is logged on this contact's timeline — including shared lines that map to multiple people.",
    relatedTo: "Supplements the primary Phone field. Use for company main lines shared by several contacts.",
  },
  notes: {
    title: "Notes",
    description: "Internal running notes about the contact.",
    usage: "Context, preferences, conversation history, and internal caveats. Keep enough detail that a teammate can pick up the relationship.",
  },
  social_links: {
    title: "Social Links",
    description: "Social media and web profiles for the contact (e.g. LinkedIn).",
    usage: "Quick jump-off for research before a call. Often populated by contact enrichment.",
  },
  custom_fields: {
    title: "Custom Fields",
    description: "Structured extra data not covered by the standard fields.",
    usage: "Holds engagement-specific attributes captured from funnels or set manually.",
  },
  last_contacted_at: {
    title: "Last Activity",
    description: "When this contact was most recently communicated with.",
    usage: "A staleness signal — a long gap is a cue to reach out. Derived from the latest logged communication; not directly editable.",
    relatedTo: "Updated automatically from the contact's communications.",
  },
  last_follow_up_task_created_at: {
    title: "Last Follow-up Task",
    description: "When the system last auto-created a follow-up task for this contact.",
    usage: "Read-only. Set by the follow-up automation so the needs-follow-up view doesn't re-flag the same contact repeatedly.",
    relatedTo: "Written by the entity follow-up cron; pairs with Last Activity for staleness tracking.",
  },
  created_at: {
    title: "Created",
    description: "When the contact record was created.",
    usage: "Read-only. Useful for cohorting contacts by when they entered the CRM.",
  },
  updated_at: {
    title: "Last Updated",
    description: "When the contact was last modified.",
    usage: "Read-only. Indicates how fresh the record is.",
  },
  created_by: {
    title: "Created By",
    description: "The user who created the contact.",
    usage: "Read-only attribution — who first added this person to the CRM.",
  },
  updated_by: {
    title: "Updated By",
    description: "The user who last modified the contact.",
    usage: "Read-only attribution — who last touched the record.",
  },
};
